import type { AffixDef, AffixId, AffixPool, NamedAffixId } from '../core/types';
import { BALANCE } from './balance';

/** Poids de tirage par pool d'affixe — MVP_DESIGN §3.3. */
export const AFFIX_POOL_WEIGHT: Record<AffixPool, number> = {
  common: 10,
  uncommon: 4,
  rare: 2,
};

/**
 * Affixes du MVP. `value` est stockée en fraction pour les pourcentages
 * (0,18 = 18 %) et en secondes pour Temporel.
 * *Mutilant* et *Pipé* (COMMENTS v0.6.0 §1.3) sont réservés au **d12** ;
 * *Pipé* est le seul affixe **tirable** unique par dé.
 *
 * v0.49.0 (COMMENTS v0.48.1 §1.4, A98-7) : *Réplicant*, affixe rare et unique
 * de tous les dés, sans valeur — une copie du dé à chaque lancer.
 *
 * v0.26.0 (A72-9) : le d20 tire dans le même pool que le d12 (tous les
 * `minSides` sont ≤ 20). Les trois affixes **nommés** des dés Prismatiques
 * (A72-14) — *Guinsoo*, *Chef de chantier*, *ArchiDicer* — sont `unique` et
 * `named` : jamais tirés au hasard, posés avec le nom fixe du dé.
 */
export const AFFIXES: AffixDef[] = [
  {
    id: 'weighted',
    name: 'Lesté',
    pool: 'common',
    minSides: 4,
    min: 0.1,
    max: 0.25,
    step: 0.01,
    unit: 'percent',
    adjective: 'plombé',
    description: '+X % de chance d’obtenir la face maximale.',
  },
  {
    id: 'incisive',
    name: 'Incisif',
    pool: 'common',
    minSides: 4,
    min: 0.15,
    max: 0.4,
    step: 0.01,
    unit: 'percent',
    adjective: 'tranchant',
    description: '+X au multiplicateur de dégâts critiques du lancer.',
  },
  {
    id: 'precise',
    name: 'Précis',
    pool: 'common',
    minSides: 4,
    min: 0.03,
    max: 0.08,
    step: 0.01,
    unit: 'percent',
    adjective: 'affûté',
    description: '+X % de chance de critique du lancer (critique global).',
  },
  {
    id: 'executioner',
    name: 'Exécutant',
    pool: 'common',
    minSides: 4,
    min: 0.25,
    max: 0.6,
    step: 0.01,
    unit: 'percent',
    adjective: 'bourreau',
    description: '+X % de dégâts sous 20 % des PV de la cible.',
  },
  {
    id: 'temporal',
    name: 'Temporel',
    pool: 'common',
    minSides: 4,
    min: 0.05,
    max: 0.3,
    step: 0.01,
    unit: 'percent',
    adjective: 'chronique',
    description: `X % de chance de gagner ${String(BALANCE.temporalSeconds).replace('.', ',')} s quand le lancer tue un ennemi.`,
  },
  {
    id: 'golden',
    name: 'Doré',
    pool: 'common',
    minSides: 4,
    min: 0.1,
    max: 0.3,
    step: 0.01,
    unit: 'percent',
    adjective: 'doré',
    description: '+X % d’or sur l’ennemi tué par ce lancer.',
  },
  {
    id: 'poisoned',
    name: 'Empoisonné',
    pool: 'uncommon',
    minSides: 6,
    min: 0.05,
    max: 0.3,
    step: 0.01,
    unit: 'percent',
    adjective: 'venimeux',
    description: 'DoT de X % des dégâts du lancer par seconde pendant 4 s (30 % max par lancer).',
  },
  {
    id: 'advantaged',
    name: 'Avantageux',
    pool: 'uncommon',
    minSides: 6,
    min: 1,
    max: 1,
    step: 1,
    unit: 'none',
    adjective: 'chanceux',
    description: 'Deux tirages, le meilleur est conservé.',
  },
  {
    id: 'mutilating',
    name: 'Mutilant',
    pool: 'uncommon',
    minSides: 12,
    min: 0.1,
    max: 0.3,
    step: 0.01,
    unit: 'percent',
    adjective: 'mutilant',
    description:
      'X % de chance par lancer touchant l’ennemi de le mutiler 3 s ' +
      '(un ennemi mutilé subit 30 % de dégâts supplémentaires).',
  },
  {
    id: 'loaded',
    name: 'Pipé',
    pool: 'rare',
    minSides: 12,
    // A89-4 : *Pipé* n'est **jamais** tiré sur un d100.
    maxSides: 20,
    min: 1,
    max: 1,
    step: 1,
    unit: 'none',
    adjective: 'pipé',
    unique: true,
    description:
      'La moitié des faces du dé sont remplacées par une face tirée à l’acquisition. ' +
      'Pipé ne peut apparaître qu’une seule fois par dé.',
  },
  {
    id: 'replicant',
    name: 'Réplicant',
    pool: 'rare',
    minSides: 4,
    min: 1,
    max: 1,
    step: 1,
    unit: 'none',
    adjective: 'répliqué',
    unique: true,
    description: 'À chaque lancer, une copie de ce dé est lancée en plus ; la copie n’est pas répliquée.',
  },
  {
    id: 'guinsoo',
    name: 'Enragé de Guinsoo',
    pool: 'rare',
    minSides: 20,
    min: 1,
    max: 1,
    step: 1,
    unit: 'none',
    adjective: 'enragé',
    unique: true,
    named: true,
    description:
      'Tous les 3 lancers, les dégâts de lancer sont appliqués deux fois. ' +
      'Un seul Dé enragé de Guinsoo équipé à la fois.',
  },
  {
    id: 'foreman',
    name: 'Chef de chantier',
    pool: 'rare',
    minSides: 20,
    min: 1,
    max: 1,
    step: 1,
    unit: 'none',
    adjective: 'bâtisseur',
    unique: true,
    named: true,
    description:
      'Débloque un tractopelle qui se charge à chaque lancer d’autant de dés Basiques que votre ' +
      'contenant. Le bouton « Lancer de tractopelle » ajoute ces dés au prochain lancer, puis ' +
      'vide le tractopelle. Un seul Dé du chef de chantier équipé à la fois.',
  },
  {
    id: 'archidicer',
    name: 'ArchiDicer',
    pool: 'rare',
    minSides: 20,
    min: 1,
    max: 1,
    step: 1,
    unit: 'none',
    adjective: 'archimage',
    unique: true,
    named: true,
    description:
      'Garantit à chaque lancer qu’au moins un sort de vos dés Maîtres est exécuté : sans sort ' +
      "de dé, l’un d’eux est tiré au hasard et appliqué. Un seul Dé de l'ArchiDicer équipé à la fois.",
  },
];

export const AFFIX_BY_ID: Record<AffixId, AffixDef> = Object.fromEntries(
  AFFIXES.map((a) => [a.id, a]),
) as Record<AffixId, AffixDef>;

/**
 * Affixes **tirables** pour un dé à `sides` faces (les affixes nommés ne le
 * sont jamais) : `minSides ≤ sides ≤ maxSides` — `maxSides` (A89-4) écarte
 * *Pipé* du d100.
 */
export function affixPoolFor(sides: number): AffixDef[] {
  return AFFIXES.filter(
    (a) => sides >= a.minSides && (a.maxSides === undefined || sides <= a.maxSides) && !a.named,
  );
}

/**
 * L'affixe `id` peut-il **agir** sur un dé à `sides` faces ? Garde-fou d'A89-4 :
 * un d100 qui porterait *Pipé* (sauvegarde modifiée) se joue sans l'effet.
 */
export function affixAppliesTo(id: AffixId, sides: number): boolean {
  const max = AFFIX_BY_ID[id]?.maxSides;
  return max === undefined || sides <= max;
}

/**
 * Les trois dés Prismatiques nommés (A72-14), dans l'ordre de tirage
 * (équiprobables) : affixe unique et nom fixe.
 */
export const NAMED_PRISMATICS: ReadonlyArray<{ affix: NamedAffixId; name: string }> = [
  { affix: 'guinsoo', name: 'Dé enragé de Guinsoo' },
  { affix: 'foreman', name: 'Dé du chef de chantier' },
  { affix: 'archidicer', name: "Dé de l'ArchiDicer" },
];

/** Nom fixe du dé nommé qui porte l'affixe `id` (« Dé enragé de Guinsoo »…). */
export function namedDieName(id: AffixId): string | null {
  return NAMED_PRISMATICS.find((n) => n.affix === id)?.name ?? null;
}
